import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// import loginBanner from '../../assets/images/login/login-banner.png';
// import sample from '../../assets/videos/background.mp4';

import { ReactComponent as TimerIcon } from '../../../assets/icons/clock-icon.svg';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomOtpInput from '../../../shared-components/custom-otp-input/CustomOtpInput';
import ConfirmBox from '../../../shared-components/modal-box/ConfirmBox';
import { getLocalStorageItem, setLocalStorageItem } from '../../../shared-functions/LocalStorage';
import authApi from '../../../utils/api/v2/auth';

/**
 * Login page function component
 */
function OTPVerification() {
  const { t, i18n } = useTranslation('loginV2');
  const navigate = useNavigate();
  const { portalType } = usePortal();
  const dispatch = useAppDispatch();
  const passwordResetDetails = getLocalStorageItem('passwordReset');
  const [OTP, setOTP] = useState<string[]>(Array(6).fill(''));
  const [disabled, setDisabled] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState('');
  const [resendTimer, setResendTimer] = useState<number>(30);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const intervalReference = useRef<NodeJS.Timeout | null>(null);

  /**
   * Navigates back to the forgot-password page after the user confirms cancellation.
   */
  const confirmCancel = () => {
    setCancelConfirmOpen(false);
    navigate('../login/forgot-password');
  };

  /**
   * handle submit function
   */
  const submitFunction = () => {
    setSubmitOpen(true);
    setOtpError(false);
    setOtpErrorMessage('');
    if (!OTP.some((digit) => digit === '')) {
      setDisabled(true);
      const otp = OTP.join('');
      setLocalStorageItem('passwordReset', {
        ...passwordResetDetails,
        otp,
      });
      authApi
        .resetPasswordOTPVerify({
          emailOrPhoneNumber: passwordResetDetails.emailOrPhoneNumber,
          countryCode: passwordResetDetails.countryCode,
          otp,
        })
        .then(() => {
          setDisabled(false);
          navigate('../login/new-password');
        })
        .catch((error) => {
          setDisabled(false);
          setOtpError(true);
          setOtpErrorMessage(t('invalidOtp'));
          if (!error.response) {
            dispatch(
              setNotificationMessage({
                message: 'No internet connection. Please check your network and try again.',
                status: true,
                type: 'error',
              }),
            );
            return;
          }
          dispatch(
            setNotificationMessage({
              message: t('invalidOtp'),
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  /**
   * Resolves which message to show under the OTP field: incomplete entry
   * takes priority, otherwise the API-reported invalid-OTP error.
   * @returns {string} translated error message
   */
  const resolveOtpErrorMessage = () => {
    if (submitOpen && OTP.some((digit) => digit === '')) {
      return t('pleaseEnterTheOtpReceivedThatWhichYouRegisteredEmailId');
    }
    return otpErrorMessage;
  };

  /**
   * Function to handle Resend OTP timer
   */
  const startResendTimer = () => {
    // Clear any previous interval before starting a new one
    if (intervalReference.current) clearInterval(intervalReference.current);

    setResendTimer(30); // Reset to 30 seconds
    intervalReference.current = setInterval(() => {
      setResendTimer((previous) => {
        if (previous <= 1) {
          if (intervalReference.current) clearInterval(intervalReference.current); // Clear interval at the end
          return 0;
        }
        return previous - 1;
      });
    }, 1000);
  };

  /**
   * Function to resend OTP
   */
  const handleResendOTP = () => {
    if (resendTimer !== 0) return;

    startResendTimer();
    setOTP(Array(6).fill(''));
    setSubmitOpen(false);
    setOtpError(false);
    setOtpErrorMessage('');

    const payload = {
      countryCode: passwordResetDetails.countryCode,
      emailOrPhoneNumber: passwordResetDetails.emailOrPhoneNumber,
      ...(portalType === PortalType.PARTNER && {
        accountCustomId: passwordResetDetails.accountCustomId
          ? passwordResetDetails.accountCustomId
          : undefined,
      }),
    };

    authApi
      .forgotPassword(payload)
      .then(() => {})
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };
  useEffect(() => {
    if (!passwordResetDetails) {
      navigate('../login/forgot-password');
    }

    startResendTimer(); // Start the timer when the component mounts
    return () => {
      if (intervalReference.current) clearInterval(intervalReference.current); // Clear interval on unmount
    };
  }, []);

  /**
   * Clears the invalid-OTP error state whenever the user edits the OTP digits.
   */
  useEffect(() => {
    setOtpError(false);
    setOtpErrorMessage('');
  }, [OTP]);

  return (
    <div className="verification-v2-wrap" dir={i18n.dir()}>
      <h1 className="title">{t('enterTheOtpBoth')}</h1>
      <div className="otp-v2-container">
        <CustomOtpInput
          length={6}
          OTP={OTP}
          setOTP={setOTP}
          type="number"
          inputLabel={t('enterOtp')}
          error={otpError || (submitOpen && OTP.some((digit) => digit === ''))}
          errorMessage={resolveOtpErrorMessage()}
        />
        <div className="resend-otp-v2-wrap">
          <div className="resend-timer-v2-wrap">
            <span className="resend-icon">
              <TimerIcon />
            </span>
            <span>00:{resendTimer < 10 ? `0${resendTimer}` : resendTimer}</span>
            <p
              role="presentation"
              onClick={handleResendOTP}
              className={`resendOtp ${resendTimer > 0 ? 'disabled' : ''}`}>
              {t('resend')}
            </p>
          </div>
          <div className="login-account">
            <span>{t('doYouHaveAnAccountAlready')}</span>
            <span role="presentation" onClick={() => navigate('../login/credentials')}>
              {t('signIn')}
            </span>
          </div>
        </div>
        <div className="otp-button-group">
          <div className="otp-button-item">
            <MyButton
              label={t('cancel')}
              buttonType="outline"
              onClickFunc={() => setCancelConfirmOpen(true)}
            />
          </div>
          <div className="otp-button-item">
            <MyButton
              label={t('proceed')}
              buttonType="submit"
              onClickFunc={submitFunction}
              loading={disabled}
              disableButton={disabled}
            />
          </div>
        </div>
      </div>
      <ConfirmBox
        open={cancelConfirmOpen}
        title={t('cancelOtpVerificationTitle')}
        subTitle={t('cancelOtpVerificationSubTitle')}
        conformSubmit={confirmCancel}
        closeModal={() => setCancelConfirmOpen(false)}
        submitLabel={t('yesCancel')}
      />
    </div>
  );
}

export default OTPVerification;
